import React from 'react';
import { unusualVitals } from '../model/entryValidation';
import type { NoteTemplate } from '../../../../constants/noteTemplates';
import type { Entry } from '../types';
import { formatMedRoute } from '../../../../constants/medRoutes';
import { clockLabel, codeLabel, NOTE_CATEGORY_LABELS } from '../model/entryMeta';
import { formatStaff, StaffMember } from '../StaffDirectoryContext';
import { EFFECTIVENESS_LABELS, MAR_STATUS_LABELS, WITHHOLD_REASON_LABELS } from './forms/MarFields';
import { TEACHING_UNDERSTANDING_LABELS } from './forms/NoteFields';
import { INTERVENTION_LABELS, RESPIRATORY_MODE_LABELS, SECRETION_LABELS } from './forms/ObservationFields';
import { POC_LABELS, SATISFACTION_LABELS } from './forms/SupervisoryNoteFields';
import type { NoteCategory } from '../../../../dtos/patientmr/charting';

type Data = Record<string, unknown>;

export interface RenderContext {
  /** Entries of the same visit (rescue med and follow-up links) */
  visitEntries?: Entry[];
  resolveStaff?: (userId: string | null | undefined) => StaffMember | undefined;
}

const has = (v: unknown): boolean => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
const s = (v: unknown): string => (v === undefined || v === null ? '' : String(v));

/** "Label: value" row, left out when there is no value */
const Row: React.FC<{ label: string; value: React.ReactNode; testId?: string }> = ({ label, value, testId }) => (
  <div data-testid={testId}><span className="font-medium">{label}:</span> {value}</div>
);

const Chip: React.FC<{ children: React.ReactNode; tone?: 'gray' | 'amber' | 'red' | 'teal' | 'purple'; testId?: string }> = ({ children, tone = 'gray', testId }) => {
  const tones = {
    gray: 'border-gray-300 bg-gray-100 text-gray-700',
    amber: 'border-amber-300 bg-amber-100 text-amber-800',
    red: 'border-red-300 bg-red-100 text-red-800',
    teal: 'border-teal-300 bg-teal-50 text-teal-800',
    purple: 'border-purple-300 bg-purple-50 text-purple-800',
  };
  return <span data-testid={testId} className={`rounded border px-1.5 py-0.5 text-xs font-semibold ${tones[tone]}`}>{children}</span>;
};

const Grid: React.FC<{ items: Array<[string, unknown, string?]>; unusual?: ReadonlySet<string> }> = ({ items, unusual }) => {
  const shown = items.filter(([, v]) => has(v));
  if (!shown.length) return null;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
      {shown.map(([label, v, unit]) => {
        // Phase 18U U10: an unusual vital is marked in text, not only by colour
        const odd = unusual?.has(label);
        return (
          <div key={label} data-unusual={odd ? 'true' : undefined}>
            <div className="text-gray-600">{label}</div>
            <div className={odd ? 'font-semibold text-amber-800' : ''}>
              {s(v)}{unit ? ` ${unit}` : ''}{odd && <span className="ml-1 text-xs font-medium">(check)</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const UNUSUAL_LABELS: Record<string, string> = { temperature: 'Temperature', bloodPressure: 'BP', pulse: 'Pulse', respiratoryRate: 'RR', o2Saturation: 'O2 Sat' };

export const VitalsContent: React.FC<{ data: Data }> = ({ data }) => {
  const pain = data.pain;
  const painText = pain && typeof pain === 'object'
    ? `${s((pain as Data).score)} (${s((pain as Data).scale)})`
    : has(pain) ? s(pain) : '';
  const unusual = new Set(unusualVitals(data as Parameters<typeof unusualVitals>[0]).map((k) => UNUSUAL_LABELS[k]));
  return (
    <Grid unusual={unusual} items={[
      ['Temperature', data.temperature, '°F'],
      ['BP', data.bloodPressure],
      ['Pulse', data.pulse],
      ['RR', data.respiratoryRate],
      ['O2 Sat', data.o2Saturation, '%'],
      ['O2 flow', data.o2FlowLpm, 'L/min'],
      ['Pain', painText],
    ]} />
  );
};

export const MarContent: React.FC<{ entry: Entry; ctx: RenderContext }> = ({ entry, ctx }) => {
  const d = entry.data as Data;
  const status = s(d.status);
  const followUpOf = s(d.followUpOf);
  const original = followUpOf ? ctx.visitEntries?.find((e) => String(e.id) === followUpOf) : undefined;
  return (
    <div className="text-sm space-y-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span><span className="font-medium">Med:</span> {s(d.medicationName)}</span>
        {Boolean(d.notOnMedList) && (
          <span data-testid="not-on-med-list-badge" className="rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
            Not on med list
          </span>
        )}
        {followUpOf ? (
          <Chip tone="teal" testId="mar-follow-up-chip">PRN follow-up{original ? ` · dose at ${clockLabel(original.observedAt || original.date)}` : ''}</Chip>
        ) : status && status !== 'GIVEN' ? (
          <Chip tone={status === 'REFUSED' ? 'red' : 'amber'} testId="mar-status-chip">{MAR_STATUS_LABELS[status as keyof typeof MAR_STATUS_LABELS] || codeLabel(status)}</Chip>
        ) : null}
      </div>
      {has(d.withholdReason) && <Row label="Reason" value={WITHHOLD_REASON_LABELS[s(d.withholdReason)] || codeLabel(s(d.withholdReason))} testId="mar-reason" />}
      {has(d.dose) && <Row label="Dose" value={s(d.dose)} />}
      {has(d.route) && <Row label="Route" value={formatMedRoute(s(d.route))} />}
      {has(d.administrationTime) && <Row label="Time" value={s(d.administrationTime)} />}
      {has(d.indication) && <Row label="PRN indication" value={s(d.indication)} testId="mar-indication" />}
      {has(d.effectiveness) && <Row label="Effect" value={EFFECTIVENESS_LABELS[s(d.effectiveness)] || codeLabel(s(d.effectiveness))} testId="mar-effect" />}
      {has(d.site) && <Row label="Site" value={s(d.site)} />}
      {has(d.reaction) && <Row label="Reaction" value={s(d.reaction)} />}
      {has(d.notes) && <Row label="Notes" value={s(d.notes)} />}
    </div>
  );
};

export const NoteContent: React.FC<{ data: Data }> = ({ data }) => {
  const category = s(data.category) as NoteCategory;
  const label = category && category !== 'GENERAL' ? NOTE_CATEGORY_LABELS[category] : '';
  const t = data.teaching && typeof data.teaching === 'object' ? (data.teaching as Data) : null;
  return (
    <div className="space-y-1">
      {label && <Chip tone="purple" testId="note-category-chip">{label}</Chip>}
      {t && (
        <div className="text-sm space-y-0.5" data-testid="teaching-content">
          {has(t.subject) && <Row label="Teaching" value={s(t.subject)} />}
          {has(t.learner) && <Row label="Learner" value={s(t.learner)} />}
          {has(t.method) && <Row label="Method" value={codeLabel(s(t.method))} />}
          {has(t.durationMinutes) && <Row label="Duration" value={`${s(t.durationMinutes)} min`} />}
          {has(t.understanding) && <Row label="Understanding" value={TEACHING_UNDERSTANDING_LABELS[s(t.understanding)] || codeLabel(s(t.understanding))} />}
        </div>
      )}
      <div className="text-sm whitespace-pre-wrap">{s(data.note)}</div>
    </div>
  );
};

export const WoundContent: React.FC<{ data: Data }> = ({ data }) => {
  const m = data.woundMeasurements && typeof data.woundMeasurements === 'object' ? (data.woundMeasurements as Data) : {};
  // Older entries stored 0 for "not measured"
  const dims = (['length', 'width', 'depth'] as const).map((k) => m[k]).filter((v) => has(v) && v !== 0);
  return (
    <div className="text-sm space-y-0.5" data-testid="wound-content">
      {has(data.woundLocation) && <Row label="Location" value={s(data.woundLocation)} />}
      {dims.length > 0 && <Row label="Size" value={`${dims.map(s).join(' × ')} ${s(m.units) || 'cm'}`} />}
      {has(data.drainage) && <Row label="Drainage" value={s(data.drainage)} />}
      {has(data.treatmentPerformed) && <Row label="Treatment" value={s(data.treatmentPerformed)} />}
      {has(data.woundDescription) && <div className="whitespace-pre-wrap">{s(data.woundDescription)}</div>}
    </div>
  );
};

export const IncidentExtras: React.FC<{ data: Data }> = ({ data }) => (
  <div className="text-sm space-y-0.5">
    {has(data.actionsTaken) && <Row label="Actions taken" value={s(data.actionsTaken)} />}
    {has(data.injuryDetails) && <Row label="Injury" value={s(data.injuryDetails)} />}
    {has(data.reportedTo) && <Row label="Reported to" value={s(data.reportedTo)} />}
    {Array.isArray(data.witnesses) && data.witnesses.length > 0 && <Row label="Witnesses" value={data.witnesses.map(String).join(', ')} />}
  </div>
);

export const RespiratoryContent: React.FC<{ data: Data }> = ({ data }) => (
  <div className="space-y-1" data-testid="respiratory-content">
    <div className="text-sm"><span className="font-medium">Mode:</span> {RESPIRATORY_MODE_LABELS[s(data.mode)] || codeLabel(s(data.mode))}{has(data.ventMode) ? ` · ${s(data.ventMode)}` : ''}</div>
    <Grid items={[
      ['Rate', data.rate],
      ['Tidal vol.', data.tidalVolumeMl, 'mL'],
      ['PIP', data.pip],
      ['PEEP', data.peep],
      ['FiO2', data.fio2, '%'],
      ['O2 flow', data.o2FlowLpm, 'L/min'],
      ['SpO2', data.spo2, '%'],
      ['Suctioned', data.suctionCount, 'x'],
      ['Secretions', has(data.secretionAmount) ? `${SECRETION_LABELS[s(data.secretionAmount)] || s(data.secretionAmount)}${has(data.secretionColor) ? `, ${s(data.secretionColor)}` : ''}` : data.secretionColor],
    ]} />
    {(data.alarmsChecked === true || data.circuitChecked === true) && (
      <div className="text-xs text-gray-700">{[data.alarmsChecked === true && 'Alarms checked', data.circuitChecked === true && 'Circuit checked'].filter(Boolean).join(' · ')}</div>
    )}
    {has(data.tolerance) && <div className="text-sm"><span className="font-medium">Tolerance:</span> {s(data.tolerance)}</div>}
    {has(data.notes) && <div className="text-sm whitespace-pre-wrap">{s(data.notes)}</div>}
  </div>
);

const durationLabel = (secs: unknown): string => {
  if (typeof secs !== 'number') return '';
  const m = Math.floor(secs / 60);
  const r = secs % 60;
  return m ? `${m} min${r ? ` ${r} s` : ''}` : `${r} s`;
};

export const SeizureContent: React.FC<{ data: Data; ctx: RenderContext }> = ({ data, ctx }) => {
  const rescue = has(data.rescueMedEntryId) ? ctx.visitEntries?.find((e) => String(e.id) === s(data.rescueMedEntryId)) : undefined;
  const rescueData = rescue?.data && typeof rescue.data === 'object' ? (rescue.data as Data) : null;
  return (
    <div className="text-sm space-y-0.5" data-testid="seizure-content">
      <Row label="Started" value={`${clockLabel(s(data.startedAt))}${has(data.durationSeconds) ? ` · ${durationLabel(data.durationSeconds)}` : ''}`} />
      {has(data.seizureType) && <Row label="Type" value={s(data.seizureType)} />}
      {has(data.description) && <div className="whitespace-pre-wrap">{s(data.description)}</div>}
      {Array.isArray(data.interventions) && data.interventions.length > 0 && (
        <Row label="Interventions" value={data.interventions.map((i) => INTERVENTION_LABELS[String(i)] || codeLabel(String(i))).join(', ')} />
      )}
      {has(data.rescueMedEntryId) && (
        <Row label="Rescue med" testId="seizure-rescue-med"
          value={rescueData ? `${s(rescueData.medicationName)}${has(rescueData.dose) ? ` ${s(rescueData.dose)}` : ''} at ${clockLabel(rescue?.observedAt || rescue?.date)}` : 'Linked MAR entry'} />
      )}
      {has(data.postIctalState) && <Row label="Post-ictal" value={s(data.postIctalState)} />}
      {Array.isArray(data.notified) && data.notified.length > 0 && <Row label="Notified" value={data.notified.map(String).join(', ')} />}
      {has(data.notes) && <Row label="Notes" value={s(data.notes)} />}
    </div>
  );
};

export const OutputContent: React.FC<{ data: Data }> = ({ data }) => (
  <div className="text-sm space-y-0.5" data-testid="output-content">
    <div>
      <span className="font-medium">{codeLabel(s(data.outputType)) || 'Output'}</span>
      {has(data.volumeMl) ? ` · ${s(data.volumeMl)} mL` : ''}
      {has(data.diaperCount) ? ` · ${s(data.diaperCount)} diaper${data.diaperCount === 1 ? '' : 's'}` : ''}
    </div>
    {has(data.characteristics) && <Row label="Characteristics" value={s(data.characteristics)} />}
    {has(data.notes) && <Row label="Notes" value={s(data.notes)} />}
  </div>
);

const PROGRESS_LABEL: Record<string, string> = { PROGRESSING: 'Progressing', MET: 'Met', NO_CHANGE: 'No change', REGRESSED: 'Regressed' };

/** Phase 20E: a note template's filled fields, in template order */
export const TemplateNoteContent: React.FC<{ template: NoteTemplate; data: Data }> = ({ template, data }) => (
  <div className="text-sm space-y-0.5" data-testid="template-note-content">
    {template.fields.filter((f) => has(data[f.key]) || typeof data[f.key] === 'boolean').map((f) => {
      const v = data[f.key];
      const shown = Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : s(v);
      return <Row key={f.key} label={f.name} value={shown} />;
    })}
  </div>
);

const TASK_STATUS_LABEL: Record<string, string> = { DONE: 'Done', REFUSED: 'Refused', NOT_NEEDED: 'Not needed' };

/** Phase 20D AIDE_TASKS: each task with its status, minutes and note */
export const AideTasksContent: React.FC<{ data: Data }> = ({ data }) => {
  const tasks = Array.isArray(data.tasks) ? (data.tasks as Data[]) : [];
  return (
    <div className="text-sm space-y-0.5" data-testid="aide-tasks-content">
      {tasks.map((t, i) => (
        <Row key={i} label={s(t.task)}
          value={`${TASK_STATUS_LABEL[s(t.status)] || s(t.status)}${has(t.minutes) ? `, ${s(t.minutes)} min` : ''}${has(t.note) ? ` (${s(t.note)})` : ''}`} />
      ))}
      {has(data.observations) && <Row label="Observations" value={s(data.observations)} />}
      {data.reportedToSupervisor === true && <Row label="Reported to supervisor" value="Yes" />}
    </div>
  );
};

/** Phase 20C THERAPY_TREATMENT: procedures with minutes, goals and progress */
export const TherapyContent: React.FC<{ data: Data }> = ({ data }) => {
  const lines = Array.isArray(data.lines) ? (data.lines as Data[]) : [];
  const goals = Array.isArray(data.goalsAddressed) ? (data.goalsAddressed as Data[]) : [];
  const procedures = [
    ...(has(data.evaluationCode) ? [`${s(data.evaluationCode)} (evaluation)`] : []),
    ...lines.map((l) => `${s(l.code)}${has(l.minutes) ? ` ${s(l.minutes)} min` : ''}`),
  ];
  return (
    <div className="text-sm space-y-0.5" data-testid="therapy-content">
      {procedures.length > 0 && <Row label="Procedures" value={procedures.join(', ')} />}
      {has(data.interventions) && <Row label="Interventions" value={s(data.interventions)} />}
      {goals.map((g, i) => (
        <Row key={i} label={i === 0 ? 'Goals' : ''} value={`${s(g.goal)}: ${PROGRESS_LABEL[s(g.progress)] || s(g.progress)}${has(g.note) ? ` (${s(g.note)})` : ''}`} />
      ))}
      {has(data.homeProgram) && <Row label="Home programme" value={s(data.homeProgram)} />}
      {has(data.patientResponse) && <Row label="Response" value={s(data.patientResponse)} />}
      {data.coTreatment === true && <Row label="Co-treatment" value={has(data.coTreatmentWith) ? s(data.coTreatmentWith) : 'Yes'} />}
      {data.telehealth === true && <Row label="Telehealth" value="Yes" />}
    </div>
  );
};

export const SupervisoryContent: React.FC<{ data: Data; ctx: RenderContext }> = ({ data, ctx }) => {
  const staff = ctx.resolveStaff?.(s(data.supervisedUserId));
  return (
    <div className="text-sm space-y-0.5" data-testid="supervisory-content">
      <Row label="Staff supervised" value={`${formatStaff(staff) || 'Staff member'}${has(data.supervisedDiscipline) && !staff?.credentials ? `, ${s(data.supervisedDiscipline)}` : ''}`} />
      <Row label="Staff present" value={data.staffPresent === true ? 'Yes' : data.staffPresent === false ? 'No' : '—'} />
      {has(data.pocFollowed) && <Row label="Plan of care followed" value={POC_LABELS[s(data.pocFollowed)] || s(data.pocFollowed)} />}
      {Array.isArray(data.competenciesObserved) && data.competenciesObserved.length > 0 && (
        <Row label="Competencies observed" value={data.competenciesObserved.map((c) => codeLabel(String(c))).join(', ')} />
      )}
      {has(data.clientSatisfaction) && <Row label="Satisfaction" value={SATISFACTION_LABELS[s(data.clientSatisfaction)] || codeLabel(s(data.clientSatisfaction))} />}
      {has(data.issues) && <Row label="Issues" value={s(data.issues)} />}
      {has(data.actions) && <Row label="Actions" value={s(data.actions)} />}
      {has(data.nextDueDate) && <Row label="Next due" value={s(data.nextDueDate)} />}
    </div>
  );
};
